import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  orderBy,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  getDocs,
  writeBatch,
  deleteDoc,
} from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import dayjs from 'dayjs';
import utc from 'dayjs-plugin-utc';
import _ from 'lodash';
import { firestore } from '../../config/firebase';
import { useAuth } from './useAuth';

dayjs.extend(utc);

export const StandardUnits = [
  {
    unitId: 0,
    unitName: 'Nollintag',
    volumeCl: 0,
  },
  {
    unitId: 1,
    unitName: 'Folköl',
    volumeCl: 50,
  },
  {
    unitId: 2,
    unitName: 'Starköl',
    volumeCl: 33,
  },
  {
    unitId: 3,
    unitName: 'Vin',
    volumeCl: 12,
  },
  {
    unitId: 4,
    unitName: 'Starkvin',
    volumeCl: 8,
  },
  {
    unitId: 5,
    unitName: 'Sprit',
    volumeCl: 4,
  },
];

export const STD_UNIT_DAY = 4;
export const STD_UNIT_WEEK = 9;
const PATIENT_ID = 'patient';
export const MAX_DAYS = 90;

export const useFirestore = () => {
  const { user, signOut } = useAuth();
  const [fireStoreReady, setFirestoreReady] = useState(false);
  const [userDoc, setUserDoc] = useState(null);
  const [consumption, setConsumption] = useState([]);
  const [chat, setChat] = useState([]);
  const [dailyComments, setDailyComments] = useState([]);

  useEffect(() => {
    setFirestoreReady(!!user);
  }, [user]);

  const addChat = async ({ text }) => {
    const chatCollection = collection(firestore, 'users', user.uid, 'chat');
    const docRef = await addDoc(chatCollection, {
      createdAt: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      author: {
        id: PATIENT_ID,
        firstName: '',
        lastName: '',
      },
      type: 'text',
      text,
      handled: false,
    });
    return docRef;
  };

  const addConsumption = async ({
    unitId,
    amount,
    datetime,
    registeredAt,
    location,
    confirmed,
  }) => {
    const consumptionCollection = collection(
      firestore,
      'users',
      user.uid,
      'consumption'
    );
    const docRef = await addDoc(consumptionCollection, {
      unitId,
      amount,
      datetime,
      registeredAt,
      location,
      confirmed,
    });
    return docRef;
  };

  const addOrUpdateDailyComment = async (date, comment) => {
    if (!date || !comment) {
      console.error(
        'useFirestore#addOrUpdateDailyComment: date and comment are required.'
      );
      return;
    }

    const dailyCommentRef = doc(
      firestore,
      'users',
      user.uid,
      'dailyComments',
      date
    );

    try {
      const docSnap = await getDoc(dailyCommentRef);

      if (docSnap.exists()) {
        await updateDoc(dailyCommentRef, { comment });
      } else {
        await setDoc(dailyCommentRef, { date, comment });
      }
    } catch (error) {
      console.error('useFirestore#addOrUpdateDailyComment', error);
    }
  };

  const addUserGoal = async ({ type, value }) => {
    const goalCollection = collection(firestore, 'users', user.uid, 'goal');
    const docRef = await addDoc(goalCollection, {
      createdAt: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
      type,
      value,
    });
    return docRef;
  };

  const subscribeChat = () => {
    const chatCollection = collection(
      doc(firestore, 'users', user.uid),
      'chat'
    );
    const unsubscribeChat = onSnapshot(chatCollection, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const { type, doc } = change;
        const c = doc.data();
        c.createdAt = dayjs(c.createdAt).format('YYYY-MM-DDTHH:mm:ss');
        if (type === 'added') {
          if (c.author?.id !== PATIENT_ID && !c.handled) {
            return;
          }
          setChat((prevChat) =>
            _.chain([{ id: doc.id, ...c }, ...prevChat])
              .sortBy('createdAt')
              .reverse()
              .value()
          );
        }
        if (type === 'modified') {
          if (c.author?.id === PATIENT_ID) {
            return;
          }
          setChat((prevChat) =>
            _.chain([{ id: doc.id, ...c }, ...prevChat])
              .sortBy('createdAt')
              .reverse()
              .value()
          );
        }
      });
    });
    return unsubscribeChat;
  };

  const subscribeConsumption = () => {
    const startDatetimeString = dayjs()
      .subtract(MAX_DAYS, 'days')
      .format('YYYY-MM-DDTHH:mm:ss');
    const consumptionCollection = collection(
      doc(firestore, 'users', user.uid),
      'consumption'
    );
    const filteredConsumptionCollection = query(
      consumptionCollection,
      where('datetime', '>', startDatetimeString),
      orderBy('datetime', 'desc')
    );
    const unsubscribeConsumption = onSnapshot(
      filteredConsumptionCollection,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const { type, doc } = change;
          if (type === 'added') {
            setConsumption((prevConsumption) =>
              _.chain([{ id: doc.id, ...doc.data() }, ...prevConsumption])
                .sortBy('datetime')
                .reverse()
                .value()
            );
          }
          if (type === 'modified') {
            setConsumption((prevConsumption) => {
              const updatedData = { id: doc.id, ...doc.data() };
              const index = _.findIndex(prevConsumption, ['id', doc.id]);

              if (index !== -1) {
                prevConsumption[index] = updatedData;
                return [...prevConsumption];
              } else {
                return prevConsumption;
              }
            });
          }
        });
      }
    );
    return unsubscribeConsumption;
  };

  const subscribeDailyComments = () => {
    const dailyCommentsCollection = collection(
      firestore,
      'users',
      user.uid,
      'dailyComments'
    );
    const orderedCommentsQuery = query(
      dailyCommentsCollection,
      orderBy('date', 'desc')
    );

    const unsubscribeDailyComments = onSnapshot(
      orderedCommentsQuery,
      (snapshot) => {
        const commentsArray = [];

        snapshot.forEach((doc) => {
          commentsArray.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setDailyComments(commentsArray);
      }
    );

    return unsubscribeDailyComments;
  };

  const subscribeUser = () => {
    const userDocRef = doc(firestore, 'users', user.uid);
    const unsubscribeUserDoc = onSnapshot(
      userDocRef,
      (doc) => {
        //console.log('--->>> User doc onSnap!', JSON.stringify(doc.data()));
        if (doc.exists()) {
          setUserDoc(doc.data());
        } else {
          console.error('No user document');
        }
      },
      (error) => {
        console.error('Error listening to document changes: ', error);
      }
    );
    return unsubscribeUserDoc;
  };

  const updateConsumptionConfirmation = async (
    consumptionId,
    confirmedValue
  ) => {
    if (!consumptionId) {
      console.error(
        'useFirestore#updateConsumptionConfirmation - No consumptionId provided.'
      );
      return;
    }

    const consumptionRef = doc(
      firestore,
      'users',
      user.uid,
      'consumption',
      consumptionId
    );

    try {
      await updateDoc(consumptionRef, {
        confirmed: !!confirmedValue,
      });
    } catch (error) {
      console.error(
        'useFirestore#updateConsumptionConfirmation - Error updating consumption confirmation:',
        error
      );
    }
  };

  const updateUser = async (userDoc) => {
    //console.log('!!! Updating user', userDoc);
    const userRef = doc(firestore, 'users', user.uid);
    try {
      if (userDoc.stdUnitDay !== undefined) {
        await addUserGoal({ type: 'day', value: userDoc.stdUnitDay });
      }
      if (userDoc.stdUnitWeek !== undefined) {
        await addUserGoal({ type: 'week', value: userDoc.stdUnitWeek });
      }
      if (userDoc.goalText !== undefined) {
        await addUserGoal({ type: 'text', value: userDoc.goalText });
      }
      await setDoc(userRef, userDoc, { merge: true });
    } catch (e) {
      console.error('***>>> useFirestore#updateUser', userDoc, e);
    }
  };

  const createUserDoc = async (firebaseAuthUser) => {
    if (firebaseAuthUser) {
      const userDocRef = doc(firestore, 'users', firebaseAuthUser.uid);
      try {
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          console.error(
            '###>>> useFirestore#createUser - user already exists',
            docSnap.data()
          );
          return;
        }
        await setDoc(userDocRef, {
          email: firebaseAuthUser.email,
          birthDate: null,
          weight: null,
          length: null,
          stdUnitDay: STD_UNIT_DAY,
          stdUnitWeek: STD_UNIT_WEEK,
          goalText: null,
        });
      } catch (e) {
        console.error(
          '###>>> useFirestore#createUser - error writing document: ',
          e
        );
      }
    }
  };

  const deleteUserAndLogout = async () => {
    if (!user) {
      console.error('No user logged in');
      return;
    }

    // Check if re-authentication might be needed
    const lastSignInTime = new Date(user.metadata.lastSignInTime).getTime();
    const currentTime = Date.now();
    if (currentTime - lastSignInTime > 10 * 60 * 1000) {
      // 10 minutes threshold
      throw new Error('Re-authentication needed');
    }

    const subcollections = ['consumption', 'chat', 'dailyComments', 'goal'];

    // Delete documents in each subcollection
    for (const subcollection of subcollections) {
      const collectionRef = collection(
        firestore,
        'users',
        user.uid,
        subcollection
      );
      const querySnapshot = await getDocs(collectionRef);
      const batch = writeBatch(firestore); // Using batch for efficient deletion

      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit(); // Commit the batch deletion
    }

    // Delete user data from Firestore
    const userDocRef = doc(firestore, 'users', user.uid);
    await deleteDoc(userDocRef);

    // Delete the user from Firebase Authentication
    await deleteUser(user);

    // Log out the user
    await signOut(auth);
  };

  return {
    addChat,
    addConsumption,
    addOrUpdateDailyComment,
    createUserDoc,
    deleteUserAndLogout,
    subscribeChat,
    subscribeConsumption,
    subscribeDailyComments,
    subscribeUser,
    updateConsumptionConfirmation,
    updateUser,
    chat,
    consumption,
    dailyComments,
    fireStoreReady,
    userDoc,
  };
};
